import {FC} from 'react'
import {Card, Form, Row, Col, Button, Spinner} from 'react-bootstrap'
import Select from 'react-select'
import {AgreementDetail, AgreementSource} from '../core/_models'
import {ColumnDef} from '@tanstack/react-table'
import DataTable from '../../../../../ui-kit/DataTable'

type Props = {
  isLoadingAgreement: boolean
  agreementOrigin: AgreementDetail
  agreementPending: AgreementDetail
  agreementType: any[]
  agreementStatus: any[]
  agreementNotification: any[]
  countries: any[]
  agreementComposition: any[]
  pendingSources: AgreementSource[]
  sourceColumns: ColumnDef<AgreementSource, any>[]
  isDifferent: (val1: any, val2: any) => boolean
  handleAgreementChange: (e: any) => void
  handlePartiesChange: (selected: any) => void
  setAgreementPending: React.Dispatch<React.SetStateAction<AgreementDetail>>
  setShowSourceModal: (show: boolean) => void
  handleCancel: () => void
  handleSubmit: () => void
  isLoadingButton: boolean
  isFormValid: boolean
}

const AgreementPendingForm: FC<Props> = ({
  isLoadingAgreement,
  agreementOrigin,
  agreementPending,
  agreementType,
  agreementStatus,
  agreementNotification,
  countries,
  agreementComposition,
  pendingSources,
  sourceColumns,
  isDifferent,
  handleAgreementChange,
  handlePartiesChange,
  setAgreementPending,
  setShowSourceModal,
  handleCancel,
  handleSubmit,
  isLoadingButton,
  isFormValid,
}) => {
  return (
    <Row className='mb-5 h-100'>
      <Col className='h-100'>
        <Card className='h-100'>
          <Card.Header className='card-header-stretch'>
            <Card.Title className='w-100'>
              <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                <div className='title d-flex align-items-center'>
                  <span className='fa fa-file-alt px-2'></span>
                  <h3 className='text-gray-700'>Pending Review Content</h3>
                </div>
              </div>
            </Card.Title>
          </Card.Header>
          <Card.Body>
            {!isLoadingAgreement ? (
              <Form className='form-compare-agreement'>
                <div className='row'>
                  <div className='col-12'>
                    <h3>General Information</h3>
                  </div>
                  <div className='col-md-6 mb-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.agreement, agreementPending.agreement)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Name
                      </span>
                    </Form.Label>
                    <Form.Control
                      placeholder='Enter full agreement name'
                      type='text'
                      name='agreement'
                      className='required'
                      value={agreementPending.agreement}
                      onChange={handleAgreementChange}
                    />
                  </div>
                  <div className='col-md-6 mb-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.acronym, agreementPending.acronym)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Short Name
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      placeholder='Enter short name'
                      name='acronym'
                      className='required'
                      value={agreementPending.acronym}
                      onChange={handleAgreementChange}
                    />
                  </div>
                  <div className='col-md-6 mb-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.type, agreementPending.type)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Type
                      </span>
                    </Form.Label>
                    <Select
                      isMulti
                      name='type'
                      placeholder='Select type(s)'
                      options={agreementType} // now [{ value, label }, ...]
                      value={
                        agreementPending.type?.map((n: string) => ({
                          value: n,
                          label: n,
                        })) || []
                      }
                      onChange={(selected) => {
                        setAgreementPending((prev) => ({
                          ...prev,
                          type: selected.map((s) => s.value), // store only the string array
                        }))
                      }}
                      styles={{
                        control: (base) => ({...base, minHeight: '80px'}),
                      }}
                    />
                  </div>
                  <div className='col-md-6 mb-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.status, agreementPending.status)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Status
                      </span>
                    </Form.Label>
                    <Form.Select
                      name='status'
                      className='required'
                      value={agreementPending.status}
                      onChange={handleAgreementChange}
                    >
                      <option value='' disabled>
                        Select status
                      </option>
                      {agreementStatus.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className='col-md-6 mb-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.wto_rta_id, agreementPending.wto_rta_id)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        WTO RTA Id
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      placeholder='Enter WTO RTA Id'
                      name='wto_rta_id'
                      value={agreementPending.wto_rta_id}
                      onChange={handleAgreementChange}
                    />
                    <a
                      href='https://rtais.wto.org/UI/PublicAllRTAList.aspx'
                      target='_blank'
                      rel='noreferrer'
                      className='text-link'
                    >
                      check the WTO database
                    </a>
                  </div>
                  <div className='col-md-6 mb-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(agreementOrigin.notification, agreementPending.notification)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Notification
                      </span>
                    </Form.Label>
                    <Select
                      isMulti
                      name='notification'
                      placeholder='Select notification(s)'
                      options={agreementNotification} // now [{ value, label }, ...]
                      value={
                        agreementPending.notification?.map((n: string) => ({
                          value: n,
                          label: n,
                        })) || []
                      }
                      onChange={(selected) => {
                        setAgreementPending((prev) => ({
                          ...prev,
                          notification: selected.map((s) => s.value), // store only the string array
                        }))
                      }}
                      styles={{
                        control: (base) => ({...base, minHeight: '80px'}),
                      }}
                    />
                  </div>
                </div>
                <hr />
                <div className='row'>
                  <div className='col-12'>
                    <h3>Date Information</h3>
                  </div>
                  <div className='col-md-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(agreementOrigin.date_signed, agreementPending.date_signed)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Date Signed
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='date'
                      name='date_signed'
                      value={agreementPending.date_signed}
                      onChange={handleAgreementChange}
                    />
                  </div>
                  <div className='col-md-4'>
                    <Form.Label
                      className={`text-primary me-0 ps-0 ${
                        agreementOrigin.id &&
                        isDifferent(
                          agreementOrigin.date_into_force,
                          agreementPending.date_into_force
                        )
                          ? 'label-highlight'
                          : ''
                      }`}
                      column
                    >
                      Date Into Force
                    </Form.Label>
                    <Form.Control
                      type='date'
                      name='date_into_force'
                      value={agreementPending.date_into_force}
                      onChange={handleAgreementChange}
                    />
                  </div>
                  <div className='col-md-4'>
                    <Form.Label
                      className={`text-primary me-0 ps-0 ${
                        agreementOrigin.id &&
                        isDifferent(
                          agreementOrigin.date_notification,
                          agreementPending.date_notification
                        )
                          ? 'label-highlight'
                          : ''
                      }`}
                      column
                    >
                      Date Notification
                    </Form.Label>
                    <Form.Control
                      type='date'
                      name='date_notification'
                      value={agreementPending.date_notification}
                      onChange={handleAgreementChange}
                    />
                  </div>
                  <div className='col-md-4'>
                    <Form.Label
                      className={`text-primary me-0 ps-0 ${
                        agreementOrigin.id &&
                        isDifferent(
                          agreementOrigin.date_of_implementation,
                          agreementPending.date_of_implementation
                        )
                          ? 'label-highlight'
                          : ''
                      }`}
                      column
                    >
                      Date of Implementation
                    </Form.Label>
                    <Form.Control
                      type='date'
                      name='date_of_implementation'
                      value={agreementPending.date_of_implementation}
                      onChange={handleAgreementChange}
                    />
                  </div>
                  <div className='col-md-4'>
                    <Form.Label
                      className={`text-primary me-0 ps-0 ${
                        agreementOrigin.id &&
                        isDifferent(
                          agreementOrigin.end_implementation,
                          agreementPending.end_implementation
                        )
                          ? 'label-highlight'
                          : ''
                      }`}
                      column
                    >
                      End Implementation
                    </Form.Label>
                    <Form.Control
                      type='date'
                      name='end_implementation'
                      value={agreementPending.end_implementation}
                      onChange={handleAgreementChange}
                    />
                  </div>
                  <div className='col-md-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(agreementOrigin.date_inactive, agreementPending.date_inactive)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Date Inactive
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='date'
                      name='date_inactive'
                      value={agreementPending.date_inactive}
                      onChange={handleAgreementChange}
                    />
                  </div>
                </div>
                <hr />
                <div className='row'>
                  <div className='col-12'>
                    <h3>Participation & Scope</h3>
                  </div>
                  <div className='col-md-6 mb-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.iso, agreementPending.iso)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Parties
                      </span>
                    </Form.Label>
                    <Select
                      isMulti
                      name='notification'
                      placeholder='Select country(s)'
                      options={countries}
                      value={
                        agreementPending.iso
                          ?.map((iso) => countries.find((option) => option.value === iso))
                          .filter(Boolean) || []
                      }
                      onChange={handlePartiesChange}
                      styles={{
                        control: (base) => ({...base, minHeight: '80px'}),
                        valueContainer: (base) => ({
                          ...base,
                          maxHeight: '90px',
                          overflowY: 'auto',
                        }),
                      }}
                    />
                  </div>
                  <div className='col-md-6 mb-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.composition, agreementPending.composition)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Composition
                      </span>
                    </Form.Label>
                    <Form.Select
                      name='composition'
                      className='required'
                      value={agreementPending.composition}
                      onChange={handleAgreementChange}
                    >
                      <option value='' disabled>
                        Select composition
                      </option>
                      {agreementComposition.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className='col-md-6 mb-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.parties_wto, agreementPending.parties_wto)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Parties WTO
                      </span>
                    </Form.Label>
                    <Form.Select
                      name='parties_wto'
                      className='required'
                      value={agreementPending.parties_wto}
                      onChange={handleAgreementChange}
                    >
                      <option value='' disabled>
                        Select parties WTO
                      </option>
                      <option value='yes'>Yes</option>
                      <option value='no'>No</option>
                    </Form.Select>
                  </div>
                  <div className='col-md-6 mb-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.region, agreementPending.region)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Region
                      </span>
                    </Form.Label>
                    <textarea
                      style={{minHeight: '80px'}}
                      className='form-control mb-3'
                      rows={2}
                      data-kt-element='input'
                      placeholder='Please select parties'
                      name='region'
                      value={agreementPending.region}
                      disabled
                    ></textarea>
                  </div>
                  <div className='col-md-6 mb-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.crossregional, agreementPending.crossregional)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Crossregional
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      className='text-capitalize'
                      name='crossregional'
                      value={agreementPending.crossregional}
                      placeholder='Please select parties'
                      disabled
                    />
                  </div>
                  <div className='col-md-6 mb-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(agreementOrigin.parties_escap, agreementPending.parties_escap)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Parties ESCAP
                      </span>
                    </Form.Label>
                    <Form.Control
                      className='text-capitalize'
                      type='text'
                      name='parties_escap'
                      value={agreementPending.parties_escap}
                      placeholder='Please select parties'
                      disabled
                    />
                  </div>
                </div>
                <hr />
                <div className='row'>
                  <div className='col-12'>
                    <h3>Others</h3>
                  </div>
                  <div className='col-md-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(agreementOrigin.language, agreementPending.language)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Language
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      name='language'
                      placeholder='Enter language code (e.g. EN)'
                      value={agreementPending.language}
                      onChange={handleAgreementChange}
                    />
                  </div>
                  <div className='col-md-4'>
                    <Form.Label
                      className={`text-primary me-0 ps-0 ${
                        agreementOrigin.id &&
                        isDifferent(
                          agreementOrigin.services_schedules,
                          agreementPending.services_schedules
                        )
                          ? 'label-highlight'
                          : ''
                      }`}
                      column
                    >
                      Service Schedule
                    </Form.Label>
                    <Form.Select
                      name='services_schedules'
                      value={agreementPending.services_schedules}
                      onChange={handleAgreementChange}
                    >
                      <option value='' disabled>
                        Select service schedule
                      </option>
                      {agreementPending.services_schedules && (
                        <option value=''>-- Clear selection --</option>
                      )}
                      <option value='yes'>Yes</option>
                      <option value='no'>No</option>
                    </Form.Select>
                  </div>
                  <div className='col-md-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.wits_id, agreementPending.wits_id)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        WITS Id
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      name='wits_id'
                      placeholder='Enter WITS Id'
                      value={agreementPending.wits_id}
                      onChange={handleAgreementChange}
                    />
                  </div>
                  <div className='col-md-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(agreementOrigin.desta_id, agreementPending.desta_id)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Desta Id
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      name='desta_id'
                      placeholder='Enter Desta Id'
                      value={agreementPending.desta_id}
                      onChange={handleAgreementChange}
                    />
                  </div>
                  <div className='col-md-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.tota_id, agreementPending.tota_id)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Tota Id
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      name='tota_id'
                      placeholder='Enter Tota Id'
                      value={agreementPending.tota_id}
                      onChange={handleAgreementChange}
                    />
                  </div>
                  <div className='col-md-4'>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.aptiad_id, agreementPending.aptiad_id)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Aptiad Id
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      name='aptiad_id'
                      placeholder='Enter Aptiad Id'
                      value={agreementPending.aptiad_id}
                      onChange={handleAgreementChange}
                    />
                  </div>
                </div>
                <hr />
                <div className='row align-items-end'>
                  <div className='col-lg-12 d-flex align-items-center justify-content-between'>
                    <h3
                      className={
                        isDifferent(agreementOrigin.sources, agreementPending.sources)
                          ? 'label-highlight'
                          : ''
                      }
                    >
                      Sources
                    </h3>
                    <Button onClick={() => setShowSourceModal(true)}>Add Source</Button>
                  </div>
                  <div className='col-lg-12'>
                    <DataTable<AgreementSource>
                      data={pendingSources}
                      columns={sourceColumns}
                      enablePagination={false}
                      enableSorting={true}
                      enableGlobalFilter={false}
                      className='my-custom-table-wrapper'
                    />
                  </div>
                </div>
                <div className='row pt-5'>
                  <div className='col-lg-12 d-flex justify-content-end gap-3'>
                    <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button
                      variant='primary'
                      onClick={handleSubmit}
                      disabled={isLoadingButton || !isFormValid}
                    >
                      {isLoadingButton ? (
                        <>
                          <Spinner animation='border' size='sm' className='me-2' />
                          Submit
                        </>
                      ) : (
                        'Submit'
                      )}
                    </Button>
                  </div>
                </div>
              </Form>
            ) : (
              <div
                className='d-flex align-items-center justify-content-center'
                style={{height: '50vh'}}
              >
                <Spinner animation='border' role='status' variant='primary' className='me-3' />
                <div className='loading-text'>
                  Loading
                  <span className='dot'>.</span>
                  <span className='dot'>.</span>
                  <span className='dot'>.</span>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default AgreementPendingForm
